// src/pages/CompletedBookings.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../context/AuthContext';
import { getUserTransactions } from '../services/api';
import BookingList from '../components/BookingList';
import FilterModal from '../components/FilterModal';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxWidth: 'md',
}));

function CompletedBookings() {
    const { currentUser, loading: authLoading } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('rentalPeriodEnd');
    const [tempSortBy, setTempSortBy] = useState('rentalPeriodEnd');
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSearchTerm, setTempSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!authLoading && currentUser) {
            // Define inline so no missing-deps warning
            const fetchBookings = async () => {
                try {
                    const response = await getUserTransactions();
                    // Filter by borrower == currentUser.id, status == completed
                    const completedBookings = response.data.filter(
                        (booking) =>
                            booking.borrower_id === currentUser.id && booking.status === 'completed'
                    );
                    setBookings(completedBookings);
                } catch (err) {
                    console.error('Error fetching completed bookings:', err);
                    setError('Failed to load completed bookings. Please try again.');
                } finally {
                    setLoading(false);
                }
            };

            fetchBookings();
        }
    }, [authLoading, currentUser]);

    const handleOpenFilterModal = () => {
        setTempSortBy(sortBy);
        setTempSearchTerm(searchTerm);
        setFilterModalOpen(true);
    };

    const handleCloseFilterModal = () => {
        setFilterModalOpen(false);
    };

    const handleUpdateFilter = () => {
        setSortBy(tempSortBy);
        setSearchTerm(tempSearchTerm);
        setFilterModalOpen(false);
    };

    const handleCancelFilter = () => {
        setTempSortBy(sortBy);
        setTempSearchTerm(searchTerm);
        setFilterModalOpen(false);
    };

    const sortedAndFilteredBookings = bookings
        .filter(
            (booking) =>
                (booking.listing_title || '')
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                (booking.listing_owner_name || '')
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            if (sortBy === 'lastActivity') {
                return new Date(b.updated_at) - new Date(a.updated_at);
            } else if (sortBy === 'rentalPeriodStart') {
                return new Date(a.start_date) - new Date(b.start_date);
            } else if (sortBy === 'rentalPeriodEnd') {
                return new Date(b.end_date) - new Date(a.end_date);
            }
            return 0;
        });

    if (authLoading || loading) {
        return (
            <StyledContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </StyledContainer>
        );
    }

    if (!currentUser) {
        return (
            <StyledContainer>
                <Typography variant="h4" gutterBottom>
                    Completed Bookings
                </Typography>
                <Typography variant="body1">
                    You need to be logged in to view your completed bookings.
                </Typography>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer>
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" flexGrow={1}>
                    Completed Bookings
                </Typography>
                <IconButton onClick={handleOpenFilterModal}>
                    <FilterListIcon />
                </IconButton>
            </Box>

            <Typography variant="body1" paragraph>
                Here are all your completed bookings. Don't forget to leave reviews for items you've
                borrowed!
            </Typography>

            <BookingList bookings={sortedAndFilteredBookings} role="borrower" />

            {sortedAndFilteredBookings.length === 0 && (
                <Typography align="center" color="textSecondary" sx={{ mt: 4 }}>
                    No completed bookings found.
                </Typography>
            )}

            <FilterModal
                open={filterModalOpen}
                onClose={handleCloseFilterModal}
                title="Filter Completed Bookings"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                tempSearchTerm={tempSearchTerm}
                setTempSearchTerm={setTempSearchTerm}
                sortBy={sortBy}
                setSortBy={setSortBy}
                tempSortBy={tempSortBy}
                setTempSortBy={setTempSortBy}
                handleApplyFilters={handleUpdateFilter}
                handleCancelFilters={handleCancelFilter}
                searchLabel="Search by item or owner name"
                sortLabel="Sort by"
            />

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert onClose={() => setError(null)} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
}

export default CompletedBookings;
